import { BookingType } from "@/data";
import { toDay } from "@/lib/datetime";
import { getHours } from "@/offering/time_picker";
import { addHours, Time } from "@/offering/time_utils";
import { getAvailableDays } from "@/offering_v2/utils/get_available_days";
import { startOfMonth, endOfMonth } from "date-fns";
import React, { useState } from "react";
import { TimeSlot } from "../date-time-picker/checkout_datetime_picker";
import {
  DateTimeValue,
  isHourlyValue,
  DailyValue,
  HourlyValue,
  DateTimePicker,
} from "../date-time-picker/date_time_picker";
import { PickingRange } from "../date_range_picker/date_range_picker";

interface DateTimeFilterDropdownProps {
  value?: DateTimeValue;
  onChange: (value?: DateTimeValue) => void;
  openAt?: string;
  closedAt?: string;
  timeSlotIncrement?: number;
}

export function DateTimeFilterDropdown({
  value: valueProp,
  onChange,
  openAt,
  closedAt,
  timeSlotIncrement,
}: DateTimeFilterDropdownProps) {
  const today = new Date();
  const startDate = isHourlyValue(valueProp)
    ? valueProp.date
    : valueProp?.startDate;
  const endDate = isHourlyValue(valueProp)
    ? valueProp.date
    : valueProp?.endDate;
  const viewingStartDate = startDate ? new Date(startDate) : today;
  const viewingEndDate = endDate ? new Date(endDate) : today;
  const [viewingRange, setViewRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>({
    startDate: startOfMonth(viewingStartDate),
    endDate: endOfMonth(viewingEndDate),
  });
  const [switchValue, setSwitchValue] = useState(
    isHourlyValue(valueProp) || !valueProp?.startDate
      ? BookingType.Hourly
      : BookingType.Daily
  );

  const availableDates = getAvailableDays(viewingRange);

  const startTimeSlots =
    isHourlyValue(valueProp) && valueProp.date
      ? getHours({
          step: timeSlotIncrement,
          startTime: openAt,
          endTime: closedAt,
          formatter: (time: Time) => time,
        })
      : [];
  const timeslots: TimeSlot[] = startTimeSlots.map((slot) => ({
    startTime: slot.label,
    endTime: getHours({
      step: timeSlotIncrement,
      startTime: addHours(slot.value, 1, true),
      endTime: closedAt,
      formatter: (time: Time) => time,
    }).map((s) => s.label),
  }));

  const calendarProps = {
    switchValue,
    timeslots,
    availableDays: availableDates.map((d) => ({
      date: toDay(d),
      availableFullDay: true,
      openAt: openAt || DEFAULT_OPEN_AT,
      closedAt: closedAt || DEFAULT_CLOSED_AT,
    })),
    onChangeMonth: (startDate: Date, endDate: Date) =>
      setViewRange({ startDate, endDate }),
    onSwitch: (value: BookingType) => {
      setSwitchValue(value);
      onChange(undefined);
    },
  };

  const handleDailyChange = (value: DailyValue) => {
    onChange({ startDate: value.startDate });

    if (value.endDate) {
      onChange({
        startDate: value.startDate,
        endDate: value.endDate,
      });
    }
  };

  const handleHourlyChange = (value: HourlyValue) => {
    if (value.date) {
      onChange({ date: value.date });
    }
    if (value.startTime) {
      onChange({
        ...valueProp,
        startTime: value.startTime,
        endTime: value.endTime,
      });
    }
  };

  return (
    <div>
      {switchValue === BookingType.Daily ? (
        <DateTimePicker<DailyValue>
          {...calendarProps}
          value={!isHourlyValue(valueProp) ? valueProp : undefined}
          onChange={handleDailyChange}
          pickingRange={PickingRange.Consecutive}
        />
      ) : (
        <DateTimePicker<HourlyValue>
          {...calendarProps}
          value={isHourlyValue(valueProp) ? valueProp : undefined}
          onChange={handleHourlyChange}
          pickingRange={PickingRange.OneDate}
        />
      )}
    </div>
  );
}

const DEFAULT_OPEN_AT = "8000";
const DEFAULT_CLOSED_AT = "2200";
